import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { addDays, subMonths } from 'date-fns';

import styles from './Prices.module.scss';
import Button from '../../../../components/Button';
import { ButtonType } from '../../../../utils/@globalTypes';

import { useParams } from 'react-router-dom';
import {
  addCertainDayPricesStatus,
  PricesSelectors,
  setActiveCertainPriceId,
} from '../../../../redux/reducers/pricesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ResponseCertainDayPriceTypeList } from '../../../../redux/types/pricesTypes';
import CertainDayPricesCard from '../../../../components/CertainDayPricesCard';
import CheckerboardPrice from '../../../../components/CheckerboardPrice';
import classNames from 'classnames';
import { TariffsSelectors } from '../../../../redux/reducers/tariffsSlice';
import { TarifTypes } from '../../../../redux/types/@types';
import RentalPriceSettings from '../../../../components/RentalPriceSettings';
import UnavailableContent from '../../../../components/UnavailableContent';
import { RentalObjectSelectors } from '../../../../redux/reducers/rentalObjectSlice';

const Prices = () => {
  const dispatch = useDispatch();
  const { mainId, rentalId } = useParams();
  const scrollToRef = useRef<HTMLDivElement>(null);

  const currentRentalObject = useSelector(
    RentalObjectSelectors.getCurrentRentalObject,
  );
  const max_places = currentRentalObject?.max_places || null;

  const currentTarif = useSelector(TariffsSelectors.getCurrentTarif);
  const currentPrices = useSelector(PricesSelectors.getPrices);

  const currentCertainDayPrices = useSelector(
    PricesSelectors.getCertainDayPrices,
  );
  const certainDayPriceStatuses = useSelector(
    PricesSelectors.getCertainDayPriceStatuses,
  );
  const activeCertainPrice = useSelector(
    PricesSelectors.getActiveCertainPriceId,
  );

  const [certainDayPricesList, setCertainDayPricesList] =
    useState<ResponseCertainDayPriceTypeList>([]);
  const [newCertainDayPricesList, setNewCertainDayPricesList] =
    useState<ResponseCertainDayPriceTypeList>([]);

  useEffect(() => {
    const url = window.location.hash;
    const interval = setInterval(() => {
      const element = document.getElementById('price_chess');
      if (element && url === '#price_chess') {
        element.scrollIntoView({ behavior: 'smooth' });
        clearInterval(interval);
      }
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const onAddCertianDayPriceClick = () => {
    if (rentalId) {
      const randomId = Math.random() + Math.random();

      dispatch(
        addCertainDayPricesStatus({
          id: randomId,
          isNew: true,
          isLoading: false,
          isSuccess: false,
        }),
      );

      setNewCertainDayPricesList((prevState) => [
        ...prevState,
        {
          id: randomId,
          start_date: '',
          end_date: '',
          base_price: '',
          extra_guest_price: '',
          min_guests: '',
          rental_object: +rentalId,
        },
      ]);
    }
  };

  useLayoutEffect(() => {
    setCertainDayPricesList(currentCertainDayPrices);
  }, [currentCertainDayPrices]);

  useEffect(() => {
    // скролл к искомой строке с настройками цен на даты
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      setTimeout(() => {
        dispatch(setActiveCertainPriceId(null));
      }, 3500);
    }
  }, [activeCertainPrice]);

  // console.log('Prices', currentPrices?.base_price?.start_date);

  return currentTarif === TarifTypes.FREE ? (
    <UnavailableContent />
  ) : rentalId ? (
    <>
      <RentalPriceSettings prices={currentPrices} />

      {(currentTarif === TarifTypes.PRO ||
        currentTarif === TarifTypes.PRO_PLUS) && (
        <>
          <h3 className={styles.contentTitle}>Календарь действующих цен</h3>
          <div className={styles.chessBlock} id="price_chess">
            <CheckerboardPrice />
          </div>
        </>
      )}

      <h3 className={styles.contentTitle}>
        Настройка цены на определенные даты (по датам заезда)
      </h3>
      {!max_places && (
        <div className={styles.error}>
          Заполните максимальное количество мест на вкладке 'Базовые настройки'
        </div>
      )}
      {(!currentPrices?.base_price.low_season_weekday_price ||
        !currentPrices?.base_price?.start_date) && (
        <div className={styles.error}>
          Заполните базовые настройки цен на текущей вкладке
        </div>
      )}

      <Button
        title="Добавить"
        type={ButtonType.PRIMARY_SMALL}
        onClick={onAddCertianDayPriceClick}
        className={styles.addPriceBtn}
        disabled={
          !currentPrices?.base_price.low_season_weekday_price ||
          !currentPrices?.base_price?.start_date
        }
      />

      {[...newCertainDayPricesList, ...certainDayPricesList].length > 0 && (
        <div className={styles.certainList}>
          {[...newCertainDayPricesList] // Добавление новой настройки вверху списка
            .map((item) => {
              const status = certainDayPriceStatuses.find(
                (status) => status.id === item.id,
              );
              return status ? (
                <CertainDayPricesCard
                  rentalId={+rentalId}
                  key={item.id}
                  data={item}
                  status={status}
                  setCertainDayPricesList={setNewCertainDayPricesList}
                  isPriceDynamic={currentPrices?.is_dynamic}
                />
              ) : null;
            })}
          {[...certainDayPricesList] //Список действующих настроек цен
            .filter(
              (item) => new Date(item.end_date) > subMonths(new Date(), 2),
            ) // не отображаем настройки на прошедшие даты
            .sort(
              (a, b) =>
                new Date(b.start_date).getTime() -
                new Date(a.start_date).getTime(),
            ) //сортируем по убыванию дат
            .map((item) => {
              const status = certainDayPriceStatuses.find(
                (status) => status.id === item.id,
              );
              const isAnimate = activeCertainPrice === item.id ? true : false;
              return status ? (
                <div
                  key={item.id}
                  ref={activeCertainPrice === item.id ? scrollToRef : null}
                  className={classNames(styles.active, {
                    [styles.animate]: isAnimate,
                  })}
                >
                  <CertainDayPricesCard
                    rentalId={+rentalId}
                    key={item.id}
                    data={item}
                    status={status}
                    setCertainDayPricesList={setNewCertainDayPricesList}
                  />
                </div>
              ) : null;
            })}
        </div>
      )}
    </>
  ) : null;
};

export default Prices;
